<template>
  <div id="contracts" class="p-4">
    <div class="row align-items-center align-item-middle pb-4">
      <div class="col-12">
        <h3 class="mb-0">Contracts</h3>
      </div>
    </div>

    <div v-loading="loading" class="contracts-container">
      <div v-if="venues.length === 0 && !loading" class="text-center py-4 text-muted">
        No Venue Contracts
      </div>

      <contract-history-display
        v-else-if="venues.length > 0"
        :venues="venues"
        :lead-event-id="leadEvent.id"
        @upload-contract="openWizard"
        @edit-contract="openWizard"
        ref="historyDisplay"
      />
    </div>

    <!-- Contract Wizard Dialog -->
    <contract-wizard
      :visible.sync="showWizard"
      :lead-event-id="leadEvent.id"
      :lead-id="leadEvent.lead_id"
      :venue-id="selectedVenueId"
      :available-venues="venues"
      :existing-contract="selectedContract"
      :initial-pdf-file="initialPdfFile"
      :client-name="leadEvent.company_name || leadEvent.client_name || ''"
      @contract-sent="onContractSent"
      @replace-contract="onReplaceContract"
      @update:visible="showWizard = $event"
    />

    <!-- Contract Sent confirmation modal -->
    <contract-sent-modal
      :visible.sync="showSentModal"
      :attachment-id="sentAttachmentId"
      :lead-event-id="leadEvent.id"
      :signed="sentAttachmentIsSigned"
    />
  </div>
</template>

<script>
import ContractWizard from '@/components/Contracts/ContractWizard.vue';
import ContractSentModal from '@/components/Contracts/ContractSentModal.vue';
import ContractHistoryDisplay from '@/views/Widgets/LeadEventDetails/Contracts/ContractHistoryDisplay.vue';
import { Message } from 'element-ui';

export default {
  name: 'LeadEventContracts',
  components: { ContractWizard, ContractSentModal, ContractHistoryDisplay },
  props: {
    leadEvent: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      showWizard: false,
      selectedVenueId: null,
      selectedContract: null,
      initialPdfFile: null,
      showSentModal: false,
      sentAttachmentId: null,
      sentAttachmentIsSigned: false,
    };
  },
  computed: {
    venues() {
      const venueMap = new Map();

      const enquiries = this.$store.getters['leadEvent/getEnquiriesByLeadEventId'](this.leadEvent.id) || [];
      enquiries.forEach(e => {
        if (e.venue && e.venue.id) {
          venueMap.set(e.venue.id, { id: e.venue.id, name: e.venue.name });
        }
      });

      const recommendations = this.$store.getters['recommendations/getRecommendationsByLeadEventId'](this.leadEvent.id) || [];
      recommendations.forEach(r => {
        if (r && r.id) {
          venueMap.set(r.id, { id: r.id, name: r.name });
        }
      });

      return Array.from(venueMap.values());
    },
  },
  async created() {
    this.loading = true;
    await Promise.all([
      this.$store.dispatch('leadEvent/getEnquiries', this.leadEvent.id),
      this.$store.dispatch('recommendations/getRecommendations', this.leadEvent.id),
    ]);
    this.loading = false;
  },
  watch: {
    // Refresh contract history whenever the wizard closes, so any changes
    // made inside (save draft, upload pdf, sign, send, etc.) are reflected
    // without requiring a full page reload.
    showWizard(isOpen, wasOpen) {
      if (wasOpen && !isOpen && this.$refs.historyDisplay) {
        this.$refs.historyDisplay.reload();
      }
    },
  },
  methods: {
    openWizard(venueId, fileOrContract) {
      this.selectedVenueId = venueId;
      // fileOrContract is a File when coming from upload modal, or a contract object for edits
      if (fileOrContract instanceof File) {
        this.initialPdfFile = fileOrContract;
        this.selectedContract = null;
      } else {
        this.initialPdfFile = null;
        this.selectedContract = fileOrContract || null;
      }
      this.showWizard = true;
    },

    async onContractSent(contractId) {
      if (this.$refs.historyDisplay) {
        try {
          await this.$refs.historyDisplay.reload();
        } catch (e) { /* ignore */ }
      }
      const latest = this.findLatestAttachment(contractId);
      this.sentAttachmentId = latest.id;
      this.sentAttachmentIsSigned = latest.signed;
      this.showSentModal = true;
      if (!this.sentAttachmentId) {
        // Fallback toast if we somehow have no downloadable attachment
        Message.success('Contract sent to client!');
      }
    },

    findLatestAttachment(contractId) {
      const data = this.$store.getters['contracts/getAllContractData'](this.leadEvent.id);
      const list = (data && data.data) || [];
      let contract = null;
      for (const v of list) {
        const versions = v.versions || (v.active_contract ? [v.active_contract] : []);
        const match = versions.find(c => c && c.id === contractId);
        if (match) { contract = match; break; }
        if (!contract && v.active_contract) contract = v.active_contract;
      }
      if (!contract) return { id: null, signed: false };
      const attachments = contract.attachments || [];
      const signed = attachments.find(a => a.type === 'client_signed_copy') || attachments.find(a => a.type === 'vc_signed_copy');
      const original = attachments.filter(a => a.type === 'initial_contract').pop();
      if (signed && signed.id) return { id: signed.id, signed: true };
      if (original && original.id) return { id: original.id, signed: false };
      return { id: null, signed: false };
    },

    onReplaceContract() {
      // Close wizard and re-open upload modal for the same venue
      this.showWizard = false;
      this.$nextTick(() => {
        if (this.$refs.historyDisplay && this.selectedVenueId) {
          this.$refs.historyDisplay.openUploadModal(this.selectedVenueId);
        }
      });
    },
  },
};
</script>
